
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, Paper, CircularProgress,
    Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    TextField, ButtonBase, Button, Collapse, IconButton, Tooltip
} from '@mui/material';
import dayjs from 'dayjs';
import InventoryIcon from '@mui/icons-material/Inventory';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DashboardSidebar from '../components/DashboardSidebar';
import { useReservationData } from '../context/ReservationDataContext';
import { fetchDispatchReservations } from '../api/dispatchService';
import { useAuth } from '../components/AuthContext';

const StockCheck: React.FC = () => {
    const { user } = useAuth();
    const isAdmin = user?.level === 1;
    const { reservations, vehicles, vehicleTypes, isLoadingReservations, isLoadingVehicles, isLoadingVehicleTypes } = useReservationData() as any;

    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
        start: dayjs().format('YYYY-MM-DD'),
        end: dayjs().add(14, 'day').format('YYYY-MM-DD')
    });

    const [revenueMonth, setRevenueMonth] = useState(dayjs().format('YYYY-MM'));
    const [revenueOpen, setRevenueOpen] = useState(true);

    // 売上用予約データ（API直接取得 — Contextのカレンダー範囲に依存しない）
    const [revenueReservations, setRevenueReservations] = useState<any[]>([]);
    const [isLoadingRevenue, setIsLoadingRevenue] = useState(false);

    const loadRevenueReservations = useCallback(async (month: string) => {
        setIsLoadingRevenue(true);
        try {
            const center = dayjs(month + '-01');
            // 選択月 ±12ヶ月分を取得
            const startDate = center.subtract(12, 'month').startOf('month').format('YYYY-MM-DD');
            const endDate = center.add(12, 'month').endOf('month').format('YYYY-MM-DD');
            const data = await fetchDispatchReservations({ start_date: startDate, end_date: endDate });
            setRevenueReservations(data || []);
        } catch (e) {
            console.error('[StockCheck] 売上用予約取得エラー:', e);
        } finally {
            setIsLoadingRevenue(false);
        }
    }, []);

    useEffect(() => {
        loadRevenueReservations(revenueMonth);
    }, [revenueMonth, loadRevenueReservations]);

    useEffect(() => {
        document.title = '在庫管理 | レンタカー予約カレンダー';
    }, []);

    const loading = isLoadingReservations || isLoadingVehicles || isLoadingVehicleTypes;

    // 車種別月間売上集計
    const revenueMatrix = useMemo(() => {
        const center = dayjs(revenueMonth + '-01');
        const months: dayjs.Dayjs[] = [];
        for (let i = -12; i <= 12; i++) {
            months.push(center.add(i, 'month'));
        }

        const vehiclesList = Object.values(vehicles) as any[];

        // 月ごとにアクティブな車両かどうか判定するヘルパー
        // active_from〜active_toの期間と対象月が重なるか + is_active + !out_of_use
        const isVehicleActiveInMonth = (v: any, month: dayjs.Dayjs) => {
            if (v.is_active === false || v.out_of_use) return false;
            const mStart = month.startOf('month');
            const mEnd = month.endOf('month');
            // active_fromがあれば、月末より後なら非アクティブ
            if (v.active_from) {
                const from = dayjs(v.active_from);
                if (from.isAfter(mEnd)) return false;
            }
            // active_toがあれば、月初より前なら非アクティブ
            if (v.active_to) {
                const to = dayjs(v.active_to);
                if (to.isBefore(mStart)) return false;
            }
            return true;
        };

        // 有効ステータス (Dashboardと同じ許可リスト方式)
        // 確定=1/booked, 貸出済み=2/done, 利用中=3/in_use
        const INCLUDED_STATUSES = new Set(['1', '2', '3', 'booked', 'done', 'in_use']);
        const isIncludedStatus = (status: any) => INCLUDED_STATUSES.has(String(status).toLowerCase());

        // 媒体分類ヘルパー
        const SELF_MEDIA = ['lp[en]', 'lp[tw]', 'lp[kr]', 'lp[ja]', 'TEL'];
        const OTHER_PORTAL = ['エアトリ', 'rcom'];
        const classifyMedia = (media: string | null | undefined): string => {
            if (!media) return 'その他';
            const m = media.trim();
            if (m === 'JA' || m.toLowerCase() === 'jalan') return 'JA';
            if (m === 'レンナビ' || m.toLowerCase() === 'rennavi') return 'レンナビ';
            if (m === 'okiren' || m.toLowerCase() === 'okiren') return 'okiren';
            if (SELF_MEDIA.includes(m)) return '自社';
            if (OTHER_PORTAL.includes(m)) return 'その他媒体';
            return 'その他';
        };
        const MEDIA_KEYS = ['JA', 'レンナビ', 'okiren', '自社', 'その他媒体', 'その他'] as const;
        type MediaKey = typeof MEDIA_KEYS[number];
        const emptyMediaCounts = (): Record<MediaKey, number> => ({ JA: 0, 'レンナビ': 0, okiren: 0, '自社': 0, 'その他媒体': 0, 'その他': 0 });

        const matrix = vehicleTypes.map((type: any) => {
            const typeVehicles = vehiclesList.filter((v: any) => v.car_type_id === type.id);

            const monthlyData = months.map((month) => {
                const mStart = month.startOf('month');
                const mEnd = month.endOf('month');
                const daysInMonth = month.daysInMonth();

                const activeTypeVehicles = typeVehicles.filter((v: any) => isVehicleActiveInMonth(v, month));
                const activeCount = activeTypeVehicles.length;

                let total = 0;
                let count = 0;
                const mediaCounts = emptyMediaCounts();

                revenueReservations.forEach((r: any) => {
                    const vehicle = vehiclesList.find((v: any) => v.id === r.vehicle_id);
                    if (!vehicle || vehicle.car_type_id !== type.id) return;
                    if (!isIncludedStatus(r.status)) return;

                    const rStart = dayjs(r.start_date);
                    if (rStart.isBefore(mStart) || rStart.isAfter(mEnd)) return;

                    count++;
                    const media = r.detail?.contact_media ?? r.contact_media ?? '';
                    const key = classifyMedia(media);
                    mediaCounts[key as MediaKey]++;

                    const price = Number(r.detail?.sum_price ?? r.sum_price ?? 0);
                    if (price > 0) {
                        total += price;
                    }
                });

                // 稼働率計算: 各アクティブ車両の予約日数合計 ÷ (台数 × 月日数)
                const totalCapacityDays = activeCount * daysInMonth;
                let occupiedDays = 0;

                activeTypeVehicles.forEach((v: any) => {
                    revenueReservations.forEach((r: any) => {
                        if (r.vehicle_id !== v.id) return;
                        if (!isIncludedStatus(r.status)) return;

                        const rStart = dayjs(r.start_date);
                        const rEnd = dayjs(r.end_date);

                        // 予約期間と月の重なり日数を計算
                        const overlapStart = rStart.isBefore(mStart) ? mStart : rStart;
                        const overlapEnd = rEnd.isAfter(mEnd) ? mEnd : rEnd;

                        if (overlapStart.isBefore(overlapEnd) || overlapStart.isSame(overlapEnd, 'day')) {
                            const days = overlapEnd.diff(overlapStart, 'day') + 1;
                            occupiedDays += Math.min(days, daysInMonth);
                        }
                    });
                });

                const occupancyRate = totalCapacityDays > 0 ? (occupiedDays / totalCapacityDays) * 100 : 0;

                return {
                    total,
                    count,
                    activeCount,
                    avg: activeCount > 0 ? Math.round(total / activeCount) : 0,
                    occupancyRate: Math.min(occupancyRate, 100),
                    occupiedDays,
                    totalCapacityDays,
                    mediaCounts,
                };
            });

            // 現在月のアクティブ台数（ヘッダー表示用）
            const currentActiveCount = typeVehicles.filter((v: any) => isVehicleActiveInMonth(v, dayjs())).length;

            return {
                type,
                vehicleCount: currentActiveCount,
                months: monthlyData,
            };
        }).filter((row: any) => row.months.some((m: any) => m.activeCount > 0));

        // 月合計行
        const totalRow = months.map((_m, mIdx) => {
            let total = 0;
            let count = 0;
            let activeCount = 0;
            let occupiedDays = 0;
            let totalCapacityDays = 0;
            const mediaCounts = emptyMediaCounts();
            matrix.forEach((row: any) => {
                total += row.months[mIdx].total;
                count += row.months[mIdx].count;
                activeCount += row.months[mIdx].activeCount;
                occupiedDays += row.months[mIdx].occupiedDays;
                totalCapacityDays += row.months[mIdx].totalCapacityDays;
                MEDIA_KEYS.forEach((k) => { mediaCounts[k] += row.months[mIdx].mediaCounts[k]; });
            });
            const occupancyRate = totalCapacityDays > 0 ? (occupiedDays / totalCapacityDays) * 100 : 0;
            return {
                total, count, activeCount, mediaCounts,
                avg: activeCount > 0 ? Math.round(total / activeCount) : 0,
                occupancyRate: Math.min(occupancyRate, 100),
                occupiedDays, totalCapacityDays,
            };
        });

        return { months, matrix, totalRow };
    }, [revenueReservations, vehicles, vehicleTypes, revenueMonth]);

    const stockMatrix = useMemo(() => {
        const start = dayjs(dateRange.start);
        const end = dayjs(dateRange.end);
        const days: any[] = [];
        let curr = start;
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
            days.push(curr);
            curr = curr.add(1, 'day');
        }

        const vehiclesList = Object.values(vehicles) as any[];

        // Filter valid vehicles
        const activeVehicles = vehiclesList.filter((v: any) =>
            v.is_active !== false &&
            !v.out_of_use
        );

        // Group by Vehicle Type
        const matrix = vehicleTypes.map((type: any) => {
            const typeVehicles = activeVehicles.filter((v: any) => v.car_type_id === type.id);
            const dailyCounts = days.map((day: any) => {
                const dayStart = day.startOf('day');
                const dayEnd = day.endOf('day');

                let availableCount = 0;
                typeVehicles.forEach((vehicle: any) => {
                    const hasReservation = reservations.some((r: any) => {
                        if (r.vehicle_id !== vehicle.id) return false;

                        // Exclude non-occupying statuses (4: cancelled, 5: nonreserved, 6: full, 7: offer)
                        const status = Number(r.status);
                        if ([4, 5, 6, 7].includes(status)) return false;

                        const rStart = dayjs(r.start_date);
                        const rEnd = dayjs(r.end_date);

                        // Overlap check
                        return rStart.isBefore(dayEnd) && rEnd.isAfter(dayStart);
                    });

                    if (!hasReservation) {
                        availableCount++;
                    }
                });
                return availableCount;
            });

            return {
                type,
                total: typeVehicles.length,
                counts: dailyCounts
            };
        });

        return { days, matrix };
    }, [reservations, vehicles, vehicleTypes, dateRange]);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
            <DashboardSidebar />

            <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 }, overflow: 'auto' }}>
                <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                            在庫管理
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            車種ごとの空き状況を確認します
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            type="date"
                            label="開始日"
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            label="終了日"
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <ButtonBase
                            sx={{
                                bgcolor: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                color: '#64748b',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                '&:hover': { bgcolor: '#f1f5f9', color: '#1e293b' }
                            }}
                        >
                            <RefreshIcon sx={{ fontSize: 18, mr: 0.5 }} />
                            更新
                        </ButtonBase>
                    </Box>
                </Box>

                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    {loading ? (
                        <Box sx={{ p: 8, textAlign: 'center' }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2, color: '#64748b' }}>在庫データを計算中...</Typography>
                        </Box>
                    ) : (
                        <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            bgcolor: '#f1f5f9',
                                            fontWeight: 800,
                                            width: 100,
                                            minWidth: 100,
                                            zIndex: 13,
                                            position: 'sticky',
                                            left: 0
                                        }}>
                                            車種 / 日付
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: '#f1f5f9',
                                            fontWeight: 800,
                                            textAlign: 'center',
                                            width: 40,
                                            minWidth: 40,
                                            zIndex: 13,
                                            position: 'sticky',
                                            left: 100
                                        }}>
                                            総数
                                        </TableCell>
                                        {stockMatrix.days.map((day: any, idx: number) => (
                                            <TableCell
                                                key={idx}
                                                sx={{
                                                    bgcolor: day.day() === 0 ? '#fee2e2' : day.day() === 6 ? '#e0f2fe' : '#f1f5f9',
                                                    fontWeight: 700,
                                                    textAlign: 'center',
                                                    minWidth: 35,
                                                    fontSize: '0.7rem',
                                                    px: 0.5
                                                }}
                                            >
                                                {day.format('M/D')}
                                                <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', opacity: 0.6, lineHeight: 1 }}>
                                                    ({['日', '月', '火', '水', '木', '金', '土'][day.day()]})
                                                </Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stockMatrix.matrix.map((row: any, rIdx: number) => (
                                        <TableRow key={rIdx} hover>
                                            <TableCell sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                bgcolor: '#fff',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 1,
                                                fontSize: '0.8rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {row.type.label}
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                    {row.type.code}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                textAlign: 'center',
                                                fontWeight: 600,
                                                bgcolor: '#f8fafc',
                                                position: 'sticky',
                                                left: 100,
                                                zIndex: 1,
                                                fontSize: '0.8rem'
                                            }}>
                                                {row.total}
                                            </TableCell>
                                            {row.counts.map((count: number, cIdx: number) => (
                                                <TableCell
                                                    key={cIdx}
                                                    sx={{
                                                        textAlign: 'center',
                                                        fontWeight: 700,
                                                        color: count === 0 ? '#ef4444' : count <= 2 ? '#f59e0b' : '#10b981',
                                                        bgcolor: count === 0 ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
                                                        fontSize: '0.9rem',
                                                        px: 0.5
                                                    }}
                                                >
                                                    {count}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>

                <Box sx={{ mt: 3, p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InventoryIcon sx={{ fontSize: 14 }} />
                        ※ 在庫数 = 「00:00 〜 23:59 まで一度も予約が入っていない車両」の合計台数です。
                        (利用外フラグのある車両は除外されています)
                    </Typography>
                </Box>

                {/* 車種別月間売上セクション（管理者のみ） */}
                {isAdmin && <Paper sx={{ mt: 4, borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    <Box
                        sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: revenueOpen ? '1px solid #e2e8f0' : 'none', cursor: 'pointer' }}
                        onClick={() => setRevenueOpen(!revenueOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon sx={{ color: '#7c3aed' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                                車種別 月間売上
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                type="month"
                                value={revenueMonth}
                                onChange={(e) => { e.stopPropagation(); setRevenueMonth(e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 160 }}
                            />
                            <IconButton size="small">
                                {revenueOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>
                    </Box>
                    <Collapse in={revenueOpen}>
                        {loading ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : (
                            <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 800, position: 'sticky', left: 0, zIndex: 13, minWidth: 90 }}>
                                                車種
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 600, position: 'sticky', left: 90, zIndex: 13, minWidth: 55, fontSize: '0.72rem', color: '#64748b', px: 0.5 }}>
                                                項目
                                            </TableCell>
                                            {revenueMatrix.months.map((m: dayjs.Dayjs, idx: number) => {
                                                const isCurrentMonth = m.isSame(dayjs(), 'month');
                                                const isCenter = m.format('YYYY-MM') === revenueMonth;
                                                return (
                                                    <TableCell
                                                        key={idx}
                                                        sx={{
                                                            bgcolor: isCurrentMonth ? '#dbeafe' : isCenter ? '#f0fdf4' : '#f1f5f9',
                                                            fontWeight: isCurrentMonth ? 800 : 700,
                                                            textAlign: 'center',
                                                            minWidth: 80,
                                                            fontSize: '0.7rem',
                                                            px: 0.5,
                                                        }}
                                                    >
                                                        {m.format('YY/M')}月
                                                        {isCurrentMonth && (
                                                            <Typography variant="caption" display="block" sx={{ fontSize: '0.55rem', color: '#2563eb', lineHeight: 1 }}>当月</Typography>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[...revenueMatrix.matrix, { type: { name: '合計', code: '' }, months: revenueMatrix.totalRow, _isTotal: true }].map((row: any, rIdx: number) => {
                                            const isTotal = row._isTotal;
                                            const stickyBg = isTotal ? '#f1f5f9' : '#fff';
                                            const fs = '0.72rem';
                                            const labelSx = { position: 'sticky' as const, left: 90, zIndex: 1, bgcolor: stickyBg, px: 0.5, py: 0.3, fontSize: fs, color: '#64748b', borderBottom: 'none' };
                                            const cellSx = (mIdx: number) => ({
                                                textAlign: 'right' as const, px: 0.5, py: 0.3, fontSize: fs, borderBottom: 'none',
                                                bgcolor: revenueMatrix.months[mIdx]?.isSame(dayjs(), 'month') ? (isTotal ? 'rgba(37,99,235,0.06)' : 'rgba(37,99,235,0.03)') : (isTotal ? '#f1f5f9' : 'transparent'),
                                            });
                                            const nameSx = { fontWeight: isTotal ? 800 : 600, color: '#1e293b', bgcolor: stickyBg, position: 'sticky' as const, left: 0, zIndex: 1, fontSize: '0.78rem', whiteSpace: 'nowrap' as const, borderBottom: 'none', verticalAlign: 'top' as const };

                                            return (
                                                <React.Fragment key={rIdx}>
                                                    {/* 合計 */}
                                                    <TableRow sx={{ borderTop: '2px solid #e2e8f0' }}>
                                                        <TableCell rowSpan={6} sx={nameSx}>
                                                            {row.type.name || row.type.label}
                                                            {row.type.code && <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.62rem' }}>{row.type.code}</Typography>}
                                                        </TableCell>
                                                        <TableCell sx={{ ...labelSx, fontWeight: 700, color: '#1e293b' }}>合計</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{ ...cellSx(mIdx), fontWeight: 700, color: '#1e293b' }}>
                                                                {md.total > 0 ? `¥${(md.total / 10000).toFixed(1)}万` : '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {/* 平均値 */}
                                                    <TableRow>
                                                        <TableCell sx={{ ...labelSx, color: '#7c3aed' }}>平均/台</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{ ...cellSx(mIdx), color: '#7c3aed' }}>
                                                                {md.avg > 0 ? `¥${(md.avg / 10000).toFixed(1)}万` : '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {/* 台数 */}
                                                    <TableRow>
                                                        <TableCell sx={labelSx}>台数</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{ ...cellSx(mIdx), color: '#64748b' }}>
                                                                {md.activeCount || '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {/* 件数 */}
                                                    <TableRow>
                                                        <TableCell sx={labelSx}>件数</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{ ...cellSx(mIdx), color: '#94a3b8' }}>
                                                                {md.count || '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {/* 媒体別 */}
                                                    <TableRow>
                                                        <TableCell sx={{ ...labelSx, fontSize: '0.65rem' }}>媒体別</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => {
                                                            const mc = md.mediaCounts || {};
                                                            const parts = [mc.JA, mc['レンナビ'], mc.okiren, mc['自社'], mc['その他媒体'], mc['その他']];
                                                            const hasAny = parts.some((v: number) => v > 0);
                                                            return (
                                                                <TableCell key={mIdx} sx={{ ...cellSx(mIdx), fontSize: '0.65rem' }}>
                                                                    {hasAny ? (
                                                                        <Tooltip title={`JA:${mc.JA||0} / レンナビ:${mc['レンナビ']||0} / okiren:${mc.okiren||0} / 自社:${mc['自社']||0} / 他媒体:${mc['その他媒体']||0} / その他:${mc['その他']||0}`} arrow>
                                                                            <span style={{ color: '#475569' }}>
                                                                                {parts.map((v: number) => v || '-').join(' / ')}
                                                                            </span>
                                                                        </Tooltip>
                                                                    ) : '-'}
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                    {/* 稼働率 */}
                                                    <TableRow>
                                                        <TableCell sx={{ ...labelSx, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>稼働率</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{
                                                                ...cellSx(mIdx),
                                                                fontWeight: 600,
                                                                borderBottom: '2px solid #e2e8f0',
                                                                color: md.occupancyRate >= 80 ? '#10b981' : md.occupancyRate >= 50 ? '#f59e0b' : '#ef4444',
                                                            }}>
                                                                {md.activeCount > 0 ? `${md.occupancyRate.toFixed(1)}%` : '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <Box sx={{ p: 1.5, borderTop: '1px solid #e2e8f0' }}>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                ※ 合計 = 予約のsum_price合計（貸出開始日ベース）。平均 = 合計 ÷ 月別アクティブ台数。キャンセル等除外。<br />
                                ※ 媒体の表示順: JA / レンナビ / okiren / 自社 / その他媒体 / その他。その他媒体 = エアトリ、rcom。その他 = その他全て。
                            </Typography>
                        </Box>
                    </Collapse>
                </Paper>}
            </Box>
        </Box>
    );
};

export default StockCheck;
